<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	//Hooks
	import { onDestroy } from 'svelte';
	//Formating
	import { format } from 'svelte-i18n';
	//Helpers
	import { getListWeatherStations } from '../../helpers/geolocalion_helper';
	import { wait } from '../../helpers/utilities';
	// Design system components
	import { Alert, Button, IconButton, Text, Label, Token } from '@Shelly/shelly-design-system';
	//Components
	import Map from '../Maps/Mapbox/MapBox.svelte';
	import MapMarker from '../Maps/Mapbox/MapMarker.svelte';
	import InfoWindow from '../Maps/Mapbox/InfoWindow.svelte';
	import Autocomplete from '../Maps/Mapbox/Autocomplete.svelte';
	import GeolocationButton from '../Maps/Mapbox/GeolocationButton.svelte';
	import ZoomButtons from '../Maps/Mapbox/ZoomButtons.svelte';
	import SearchInThisArea from '../Maps/Mapbox/SearchInThisArea.svelte';
	//Stores
	import { user_settings, profile_data } from 'src/stores/account/user';
	//Types
	import type { WeatherServiceResponse, WeatherStations } from '../../types/requests/services/weather';

	export let weatherStationSaveData;
	export let resizeMap = false;
	export let mapParentSize = false;

	const dispatch = createEventDispatcher();

	let activeToken = 1;
	if (!$profile_data.weather_temp_unit || String($profile_data.weather_temp_unit) === 'C') {
		activeToken = 1;
	} else {
		activeToken = 2;
	}

	let markers = [],
		mapCanvas: Map,
		infoWindow: InfoWindow,
		selectedMarker: {
			id?: number;
			lat: number;
			lng: number;
			data?: WeatherStations;
		},
		waitForResponse = false,
		mapProps: {
			center?: [number, number];
			zoom?: number;
		} = {},
		stationName: string = null;

	const getWeatherStations = async (e: any) => {
		if (infoWindow) infoWindow.close();
		if (waitForResponse) return;
		markers = [];
		waitForResponse = true;
		const res: WeatherServiceResponse = await getListWeatherStations(e.detail);
		if (res?.isok && res.data?.length) {
			markers = res.data.sort((a, b) => a.lat - b.lat);
			if (markers.length) mapCanvas.fitMapToMarkers(markers);
		}
		waitForResponse = false;
	};
	const onClickMarker = (e: CustomEvent) => {
		selectedMarker = e.detail;
		if (infoWindow) infoWindow.open(selectedMarker);
	};
	const addStation = async () => {
		if (mapProps?.center?.[0] == selectedMarker.lng && mapProps?.center?.[1] == selectedMarker.lat) {
			infoWindow.close();
			return;
		}
		mapProps.center = [selectedMarker.lng, selectedMarker.lat];
		weatherStationSaveData = {
			stationID: selectedMarker.data.id,
			weatherID: selectedMarker.data.wid,
			stationName: selectedMarker.data.name,
			lat: selectedMarker.lat,
			lng: selectedMarker.lng,
		};
		stationName = weatherStationSaveData.stationName;
		await wait(500);
		infoWindow.close();
	};
	const onLoadMap = () => {
		if (mapProps?.center) {
			getWeatherStations({
				detail: { lat: mapProps.center[1], lon: mapProps.center[0] },
			});
		}
	};

	if ($user_settings?.weather_station?.data?.stationName) {
		mapProps = {
			center: [$user_settings.weather_station.data.lng, $user_settings.weather_station.data.lat],
			zoom: 10,
		};
		stationName = $user_settings.weather_station.data.stationName;
	}

	onDestroy(() => {
		markers = [];
	});

	const updateMapSize = () => {
		if (!mapCanvas || !mapParentSize) return;
		mapCanvas.updataMapSizeDependOnParent();
		dispatch('open');
	};

	$: selectedWeatherStation =
		mapProps?.center?.[0] == selectedMarker?.data?.lon &&
		mapProps?.center?.[1] == selectedMarker?.data?.lat;
	$: (resizeMap, updateMapSize());

	const saveDateFormat = async (tempUnit, activeCode?) => {
		activeToken = activeCode;
		dispatch('set-temp-unit', { tempUnit });
	};
</script>

<Text element="h2" class="mb-s">
	{$format('text_title_thermostat_temperature_unit', {
		default: 'Temperature unit',
	})}
</Text>
<div class="d-flex align-items-center">
	<Token
		id="token-tgjokb3"
		class="mr-s"
		selected={activeToken === 1}
		title="°C"
		size="m"
		on:click={() => saveDateFormat('C', 1)}
	/>
	<Token
		id="token-m3ntpeg"
		selected={activeToken === 2}
		title="°F"
		size="m"
		on:click={() => saveDateFormat('F', 2)}
	/>
</div>

<hr class="mt-l mb-l" />

<Text element="h2" class="mb-s">
	{$format('label_location', {
		default: 'Location',
	})}
</Text>
{#if stationName}
	<Alert
		class="mb-s"
		customIcon="fa-solid fa-location-check text-success"
		type="success"
		heading={$format('current_weather_station', {
			default: 'Current Weather Station:',
		})}
		isHeadingBold
		subHeading={`${stationName}${''}`}
	/>
{:else}
	<Alert
		class="mb-s"
		type="info"
		customIcon="fa-solid fa-crosshairs text-primary"
		heading={$format('weather_info_alert', {
			default:
				'Click the button to allow to get your location, use the search or click the ‘Search this area’ button for weather stations to appear.',
		})}
	/>
{/if}

<div class="map-container">
	<Map
		on:click={getWeatherStations}
		on:load={onLoadMap}
		bind:this={mapCanvas}
		{...mapProps}
		{mapParentSize}
	>
		{#each markers as marker (marker.id)}
			{@const selected =
				mapProps?.center?.[0] == marker.lon && mapProps?.center?.[1] == marker.lat}
			<MapMarker
				id={marker.id}
				lat={marker.lat}
				lng={marker.lon}
				data={marker.data || marker}
				{selected}
				on:click={onClickMarker}
			>
				<IconButton
					aria-label={$format('button_select_weather_station', {
						default: 'Select Weather Station',
					})}
					class="align-self-center"
					icon="fas fa-location-{selected ? 'check' : 'dot'}"
					variant={selected ? 'success' : 'primary'}
				/>
			</MapMarker>
		{/each}
		<InfoWindow bind:this={infoWindow}>
			<Text styleType="body-small" class="mb-xs text-center">
				{$format('weather_station', {
					default: 'Weather Station:',
				})}
			</Text>
			<Text styleType="h2" class="text-center mb-m">
				{selectedMarker ? selectedMarker.data.name : ''}
			</Text>
			{#if selectedWeatherStation}
				<Label
					class="width-full"
					textClass="text-white"
					size="m"
					type="success"
					icon="fas fa-check text-white"
					value={$format('selected', { default: 'Selected' })}
				/>
			{:else}
				<Button size="m" width="full" variant="primary" on:click={addStation}>
					{$format('select', { default: 'Select' })}
				</Button>
			{/if}
		</InfoWindow>
		<Autocomplete on:select={getWeatherStations} />
		<SearchInThisArea isLoading={waitForResponse} on:change={getWeatherStations} />
		<ZoomButtons />
		<GeolocationButton on:change={getWeatherStations} flyToMap={false} />
	</Map>
</div>

<style>
	.map-container {
		height: 100%;
		display: flex;
		flex: auto;
		min-height: 0;
		position: relative;
	}
</style>
